import { environment } from '@root/src/environments/environment';
import {BaseLayerType, IBaseLayerConfig} from '../controllers/map-layers.controller';
import {MapLayersController} from '../openstreetmap.controller';

export type OsmBaseMapType = 'plan' | 'google';
export type OsmOverlaySourceType = 'sentinel' | 'landsat' | 'uav';

export const OSM_SATELLITE_URLS: Record<'sentinel' | 'landsat', string> = {
  sentinel: environment.api + '/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}&layer=Sentinel2_2026&coordinateSystem=0',
  landsat: environment.api + '/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}&layer=Landsat_2026&coordinateSystem=0',
};

export const OSM_SATELLITE_MENU_ITEMS = [
  {label: 'Google', value: 'google'},
  {label: 'Sentinel', value: 'sentinel'},
  {label: 'Landsat', value: 'landsat'},
] as const;

export interface OsmMapBaselayerHost {
  currentBaseLayer: OsmBaseMapType;
  currentSatelliteLayer: OsmOverlaySourceType | null;
  /** When `all`, the year mosaic ({@link OSM_SATELLITE_URLS}) is shown; range uses per-product tiles only. */
  satelliteDisplayMode?: 'range' | 'all' | null;
  currentSatelliteLabel: string;
  satelliteLayerOpacity?: number;
  mapLayers: IBaseLayerConfig;
  isMapInitialized: boolean;
  layersController: MapLayersController | undefined;
}

/** Switch the underlying map between Plan and Google — does not affect satellite overlays. */
export function switchOsmBaseLayer(host: OsmMapBaselayerHost, layerType: OsmBaseMapType): void {
  if (host.currentBaseLayer === layerType) {
    return;
  }

  host.currentBaseLayer = layerType;
  const layer = host.mapLayers[layerType];

  if (host.isMapInitialized && host.layersController) {
    host.layersController.switchBaseLayer(layerType, layer.url as string, {
      attribution: layer?.attribution,
      maxZoom: 18,
      minZoom: 1,
      subdomains: (layer as {subdomains?: string[]})?.subdomains,
      opacity: 1,
    });
  }
}

/** Apply or remove a Sentinel / Landsat / UAV overlay on top of the current base layer. */
export function switchOsmOverlayLayer(
  host: OsmMapBaselayerHost,
  overlayType: OsmOverlaySourceType | null,
): void {
  host.currentSatelliteLayer = overlayType;

  if (!overlayType) {
    host.currentSatelliteLabel = 'Satellite';
    host.layersController?.clearOverlayLayer();
    return;
  }

  host.currentSatelliteLabel =
    overlayType === 'sentinel' ? 'Sentinel' : overlayType === 'landsat' ? 'Landsat' : 'Drone';

  if (!host.isMapInitialized || !host.layersController) {
    return;
  }

  if (overlayType === 'uav') {
    host.layersController.clearOverlayLayer();
    return;
  }

  if (host.satelliteDisplayMode !== 'all') {
    host.layersController.clearOverlayLayer();
    return;
  }

  const url = OSM_SATELLITE_URLS[overlayType];
  const options: {
    attribution: string;
    maxZoom: number;
    minZoom: number;
  } = {
    attribution: overlayType === 'sentinel' ? '© ESA/Copernicus' : '© USGS/NASA Landsat',
    maxZoom: 18,
    minZoom: 1,
  };

  host.layersController.switchOverlayLayer(url, {
    ...options,
    opacity: (host.satelliteLayerOpacity ?? 100) / 100,
  });
}

/** @deprecated Use {@link switchOsmBaseLayer} — kept for callers that still pass BaseLayerType. */
export function switchOsmMapLayer(host: OsmMapBaselayerHost, layerType: BaseLayerType): void {
  if (layerType === 'plan' || layerType === 'google') {
    switchOsmBaseLayer(host, layerType);
  }
}

/** @deprecated Use {@link switchOsmOverlayLayer} */
export function switchOsmSatelliteLayer(host: OsmMapBaselayerHost, satelliteType: BaseLayerType): void {
  if (satelliteType === 'sentinel' || satelliteType === 'landsat') {
    switchOsmOverlayLayer(host, satelliteType);
  } else if (satelliteType === 'google') {
    switchOsmBaseLayer(host, 'google');
  }
}

export function resetOsmSatelliteUi(host: OsmMapBaselayerHost): void {
  switchOsmOverlayLayer(host, null);
}
